import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Container,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import DeliveryMethodPreview from "components/DeliveryMethodPreview";
import LazyTable, { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { createContext, useContext, useMemo, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  SkuDeliverableInfo,
  Skus,
} from "services/KnowfullyAdminClient";
import {
  DeliveryMethodSettingCodeConstants,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import { SkuDeliveryMethodWithId, SkuFormProps } from "types/sku-related-types";
import {
  DeliveryMethodPreviewBehavior,
  getDeliveryMethodIcon,
  getDeliveryMethodPreviewBehavior,
  getSettingValue,
  getShowKey,
  isInxpoDeliveryMethod,
} from "./deliveryMethodPresentation";
import SkuDeliveryMethodCreateForm from "./SkuDeliveryMethodCreateForm";
import SkuDeliveryMethodUpdateForm from "./SkuDeliveryMethodUpdateForm";

type SkuDeliveryMethodsProps = {
  sku: Skus;
  isReadonly?: boolean;
};

const styles = {
  contentType: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  missingKey: {
    color: "warning.main",
  },
};

type CellActions = {
  onPreview: (value: SkuDeliverableInfo) => void;
  onEdit: (value: SkuDeliverableInfo) => void;
  onDelete: (value: SkuDeliverableInfo) => void;
};

const CellActionsContext = createContext<CellActions | null>(null);

const useCellActions = (): CellActions => {
  const actions = useContext(CellActionsContext);

  if (!actions) {
    throw new Error("Delivery method cells require a CellActionsContext provider");
  }

  return actions;
};

const DragHandleCell = () => <DragHandleIcon />;

const ContentTypeCell = ({ value }: { value: SkuDeliverableInfo }) => (
  <Box sx={styles.contentType}>
    {getDeliveryMethodIcon(value.deliveryMethodId)}
    {value.deliveryMethodName}
  </Box>
);

const ValidationCell = ({ value }: { value: SkuDeliverableInfo }) => {
  const { onPreview } = useCellActions();

  if (
    getDeliveryMethodPreviewBehavior(value.deliveryMethodId) ===
    DeliveryMethodPreviewBehavior.None
  ) {
    if (!isInxpoDeliveryMethod(value.deliveryMethodId)) {
      return null;
    }

    const showKey = getShowKey(value.settingValues);

    return showKey ? (
      <Typography variant="body2">{showKey}</Typography>
    ) : (
      <Typography variant="body2" sx={styles.missingKey}>
        <T id="missingKey" />
      </Typography>
    );
  }

  return (
    <Button
      variant="text"
      color="primary"
      size="small"
      startIcon={<VisibilityIcon />}
      onClick={() => onPreview(value)}
    >
      <T id="preview" />
    </Button>
  );
};

const ActionsCell = ({ value }: { value: SkuDeliverableInfo }) => {
  const { onEdit, onDelete } = useCellActions();

  return (
    <Box display="flex" justifyContent="right">
      <IconButton size="small" onClick={() => onEdit(value)}>
        <EditIcon />
      </IconButton>
      <IconButton size="small" onClick={() => onDelete(value)}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const SkuDeliveryMethods = ({
  sku,
  onSkuUpdated,
  isReadonly,
  onDirty,
  onClean,
  ...rest
}: SkuDeliveryMethodsProps & SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService, skuDeliverableService } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuDeliverableInfo>();
  const [editing, setEditing] = useState<SkuDeliverableInfo>();
  const [previewing, setPreviewing] = useState<SkuDeliverableInfo>();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);

  const invalidateQueries = () => {
    queryClient.invalidateQueries(["skuDeliveryMethods", sku.id]);
  };

  const handleDelete = async () => {
    const id = deleting?.skuDeliverableId;
    if (id) {
      try {
        await registerPromise(skuService.deleteSkuDeliveryMethod(id));
        invalidateQueries();
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuDeliveryMethodDeleted" }),
            Severity.Warning
          )
        );
        setDeleting(undefined);
      } catch (err: any) {
        console.log(err);
      }
    }
  };

  const { data, isFetching } = useStrictQuery(
    ["skuDeliveryMethods", sku.id],
    () => skuService.getSkuDeliveryMethods(sku.id!)
  );

  const skuDeliveryMethods = useMemo(
    () =>
      (data?.data ?? []).map((dm) => ({
        ...dm,
        id: dm.skuDeliverableId!,
      })) as SkuDeliveryMethodWithId[],
    [data?.data]
  );

  const skuDeliveryMethodsMaxSequence = useMemo(
    () =>
      [...skuDeliveryMethods]
        .sort((a, b) => (a?.sequence! > b?.sequence! ? 1 : -1))
        .at(skuDeliveryMethods.length - 1)?.sequence,
    [skuDeliveryMethods]
  );

  const onSkuDeliveryMethodCreated = () => {
    invalidateQueries();
    createOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuDeliveryMethodCreated" }))
    );
  };

  const onSkuDeliveryMethodUpdated = () => {
    invalidateQueries();
    setEditing(undefined);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuDeliveryMethodUpdated" }))
    );
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const reordered = [...skuDeliveryMethods];

    const element = skuDeliveryMethods[result.source.index];
    reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, element);

    await skuService.updateSkuDeliveryMethodSequence(sku.id!, reordered);
    invalidateQueries();
  };

  const handlePreviewClick = async (value: SkuDeliverableInfo) => {
    if (
      getDeliveryMethodPreviewBehavior(value.deliveryMethodId) !==
      DeliveryMethodPreviewBehavior.NewTab
    ) {
      setPreviewing(value);
      return;
    }

    try {
      const response = await registerPromise(
        skuDeliverableService.accessSkuDeliverable(value.skuDeliverableId!)
      );

      if (!response.data) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "previewUnavailable" }),
            Severity.Error
          )
        );
        return;
      }

      window.open(response.data, "_blank", "noopener,noreferrer");
    } catch {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "previewUnavailable" }), Severity.Error)
      );
    }
  };

  const cellActions: CellActions = {
    onPreview: handlePreviewClick,
    onEdit: setEditing,
    onDelete: setDeleting,
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "",
      align: "left",
      type: "node",
      View: ContentTypeCell,
      noSort: true,
      label: t({ id: "contentType" }),
    },
    {
      accessor: "",
      align: "left",
      type: "node",
      View: ValidationCell,
      noSort: true,
      label: t({ id: "validation" }),
    },
    {
      accessor: "displayText",
      align: "left",
      type: "text",
      noSort: true,
      label: t({ id: "displayText" }),
    },
  ];

  if (!isReadonly) {
    cells.push({
      accessor: "",
      width: 1,
      type: "node",
      View: ActionsCell,
      label: t({ id: "actions" }),
      noSort: true,
    });

    cells.unshift({
      accessor: "",
      width: 1,
      type: "node",
      View: DragHandleCell,
      label: "",
      noSort: true,
    });
  }

  return (
    <Container>
      {!isReadonly && (
        <Box my={2} textAlign="right">
          <Button
            variant="outlined"
            type="submit"
            color="primary"
            onClick={createOpenActions.setTrue}
            data-cy="addDeliveryMethodButton"
          >
            <T id="skuDeliveryMethodCreateButtonLabel" />
          </Button>
        </Box>
      )}

      <CellActionsContext.Provider value={cellActions}>
        <LazyTable
          {...{
            cells,
            rows: skuDeliveryMethods,
            rowCount: skuDeliveryMethods.length,
            page: 0,
            setPage: () => {},
            rowsPerPage: skuDeliveryMethods.length,
            setRowsPerPage: () => {},
            order: "asc",
            setOrder: () => {},
            orderBy: "",
            setOrderBy: () => {},
            loading: isFetching,
            dense: true,
            allowDragAndDrop: !isReadonly,
            onDragAndDropEnd: handleDragEnd,
            allowPagination: false,
          }}
        />
      </CellActionsContext.Provider>

      <DeleteEntityDialog
        dialogTitleKey="skuDeliveryMethodDeleteTitle"
        contentKey="skuDeliveryMethodDeleteQuestion"
        open={!!deleting}
        handleDelete={handleDelete}
        onCancel={() => setDeleting(undefined)}
      />

      <Dialog
        open={!!editing}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
        onClose={() => setEditing(undefined)}
      >
        {editing && (
          <SkuDeliveryMethodUpdateForm
            sku={sku}
            skuDeliveryMethod={editing}
            onSkuDeliveryMethodUpdated={onSkuDeliveryMethodUpdated}
            onCancel={() => setEditing(undefined)}
            onDirty={onDirty}
            onClean={onClean}
          />
        )}
      </Dialog>

      <Dialog
        open={createOpen}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
        onClose={createOpenActions.setFalse}
      >
        <SkuDeliveryMethodCreateForm
          sku={sku}
          onSkuDeliveryMethodCreated={onSkuDeliveryMethodCreated}
          onCancel={createOpenActions.setFalse}
          skuDeliveryMethodsMaxSequence={skuDeliveryMethodsMaxSequence}
          {...rest}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />

      <DeliveryMethodPreview
        open={!!previewing}
        onClose={() => setPreviewing(undefined)}
        skuDeliverableId={previewing?.skuDeliverableId}
        deliveryMethodId={previewing?.deliveryMethodId}
        organizationId={sku.ownerOrganizationId}
        displayText={previewing?.displayText}
        width={getSettingValue(previewing?.settingValues, DeliveryMethodSettingCodeConstants.Width)}
        height={getSettingValue(previewing?.settingValues, DeliveryMethodSettingCodeConstants.Height)}
        skuTitle={sku.title}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuDeliveryMethods);
